// apps/admin/src/pages/billing/MerchantBillingPage.tsx
//
// Phase 4.1 Wave 14 Task 43 — Admin A7: per-merchant Billing tab composing
// BillingModeSelector + BillingConfigForm + ModeSettlementDialog +
// MerchantInvoiceHistory.
//
// vg-binding: CONTEXT:decisions=P4.1.D-11,P4.1.D-10,P4.1.D-24,P4.1.D-29,P4.1.D-30,P4.1.D-21
// vg-binding: INTERFACE-STANDARDS:error-shape (toast surfaces
//   error.user_message || error.message; never axios.message)
//
//  (Payment Methods MVP — Amendment #8 2026-05-04): per-merchant
// 2-level design. The BillingConfigForm payment_methods checkboxes here
// expose 2 broad categories (BANK_TRANSFER, GATEWAY); the 9 specific
// plugins live platform-wide in GatewaySettingsPage.tsx. See that file
// for Tier 1 / Tier 2 / hot-swap registry.
//
// Per CONTEXT.md A7 (admin per-merchant Billing surface).
// Per CONTEXT.md #3 (PUT /admin/merchants/:id/billing-mode contract).
// Per CONTEXT.md (segregation of duties — mode switch admin-only
//   billing config editable by accountant).
// Per CONTEXT.md (net-settlement option apply_credits_then_switch
//   when switching mode with outstanding debt + active credit_memos).
// Per CONTEXT.md (due_day_type CALENDAR vs BUSINESS).
// Per INTERFACE-STANDARDS.md § FE error envelope (message priority
//   error.user_message || error.message || message || network_fallback).
// Per design ref:
//   .vg/phases/04.1-invoice-billing-net-terms/design/screenshots/merchant-billing-page.default.png
//   sha256 36d765e2a155e3856041f6bd65e394d81e2ffa9b97b154bfed91a8c1ad9c3d05
//
// Goals covered:.
//
// Endpoints consumed (verbatim from API-CONTRACTS slices):
//   GET   /api/v1/admin/merchants?search=&pageSize=1                              (resolve merchant for header)
//   GET   /api/v1/admin/merchants/:id/billing-config                              (current mode + config + soft cap)
//   POST  /api/v1/admin/merchants/:id/billing-config                              (Idempotency-Key required)
//   PUT   /api/v1/admin/merchants/:id/billing-mode                                (Idempotency-Key required; admin only)
//   GET   /api/v1/admin/credit-memos?merchant_id=&status=ACTIVE                   (settlement preview)
//   GET   /api/v1/admin/invoices?merchant_id=&page=&pageSize=                     (invoice history)
//
// Reuses InvoiceListPage + InvoiceDetailPage patterns:
//   - apiClient() narrow-cast to LooseClient
//   - useToast() / useAuth() from @printway/ui
//   - Dialog primitive from @printway/ui

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { apiClient } from '@printway/shared/api';
import {
  Card,
  CardContent,
  Pagination,
  cn,
  useAuth,
  useToast,
  PageHeader,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import BillingModeSelector from '../../features/billing/BillingModeSelector';
import BillingConfigForm from '../../features/billing/BillingConfigForm';
import ModeSettlementDialog from '../../features/billing/ModeSettlementDialog';
import type { SettlementAction as DialogSettlementAction } from '../../features/billing/ModeSettlementDialog';

// ---------------------------------------------------------------------------
// Wire types — slim FE mirror of API-CONTRACTS Zod schemas. Hand-rolled
// because shared/api/types.generated.ts is still a stub for Phase 4.1
// (codegen lands at /vg:test). Field names match BLOCK 2 of each contract.
// ---------------------------------------------------------------------------

type BillingMode = 'PREPAID' | 'CREDIT' | 'INVOICE';
type InvoiceCycle = 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';
type DueDayType = 'CALENDAR' | 'BUSINESS';
type PaymentMethod = 'BANK_TRANSFER' | 'GATEWAY';
type SettlementAction = 'convert' | 'refund_bank' | 'apply_credits_then_switch';

type InvoiceStatusEnum =
  | 'DRAFT'
  | 'SENT'
  | 'PENDING_APPROVAL'
  | 'PAID'
  | 'PARTIAL'
  | 'OVERDUE'
  | 'VOIDED';

type CreditMemoStatus =
  | 'ACTIVE'
  | 'PARTIALLY_APPLIED'
  | 'FULLY_APPLIED'
  | 'CONVERTED'
  | 'REFUNDED'
  | 'PENDING_APPROVAL';

interface BillingConfig {
  cycle: InvoiceCycle;
  due_days: number;
  due_day_type: DueDayType;
  soft_limit_usd_cents: number | null;
  payment_methods: PaymentMethod[];
  last_cycle_generated_at: string | null;
}

interface SoftCapUsage {
  gross_outstanding_usd_cents: number;
  net_outstanding_usd_cents: number;
  soft_limit_usd_cents: number | null;
  over_cap: boolean;
}

interface BillingConfigResponseBody {
  merchant_id: string;
  billing_mode: BillingMode;
  billing_mode_version: number;
  billing_config: BillingConfig | null;
  soft_cap_usage: SoftCapUsage;
}

interface MerchantSummary {
  id: string;
  name: string;
  email: string;
  billing_mode: BillingMode;
  billing_mode_version: number;
  outstanding_usd_cents: number;
  has_overdue_invoice: boolean;
  /** Withdraw permission only on Enterprise tier. */
  tier?: 'titanium' | 'platinum' | 'diamond' | 'enterprise' | 'e2' | 'e3';
  /** Mirror of merchant.wallet.withdraw_enabled (read-only on this page). */
  withdraw_enabled?: boolean;
}

interface MerchantListResponseBody {
  merchants: MerchantSummary[];
  pagination: PaginationMeta;
}

interface PaginationMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface CreditMemoRow {
  id: string;
  amount_usd_cents: number;
  status: CreditMemoStatus;
  created_at: string;
  applied_amount?: number | null;
}

interface CreditMemoListResponseBody {
  credit_memos: CreditMemoRow[];
  pagination: PaginationMeta;
  totals: {
    active_count: number;
    pending_approval_count: number;
    active_remaining_usd_cents: number;
  };
}

interface AdminInvoiceRow {
  id: string;
  invoice_number: string;
  status: InvoiceStatusEnum;
  period_start: string;
  period_end: string;
  due_at: string | null;
  due_local_date: string | null;
  total_usd_cents: number;
  paid_amount_usd_cents: number;
  remaining_amount_usd_cents: number;
  created_at: string;
}

interface AdminInvoiceListResponseBody {
  invoices: AdminInvoiceRow[];
  pagination: PaginationMeta;
  totals: {
    outstanding_usd_cents: number;
    overdue_count: number;
  };
}

interface ApiErrorEnvelope {
  ok?: false;
  success?: false;
  error?: {
    code?: string;
    message?: string;
    user_message?: string;
    details?: unknown;
    field_errors?: Record<string, string[]>;
  };
}

type LooseClient = {
  GET: (
    path: string,
    init?: { headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  POST: (
    path: string,
    init: { body?: unknown; headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  PUT: (
    path: string,
    init: { body?: unknown; headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// MODE_OPTIONS / CYCLE_OPTIONS / DUE_DAY_TYPE_OPTIONS / PAYMENT_METHOD_OPTIONS
// moved into apps/admin/src/features/billing/{BillingModeSelector,BillingConfigForm}.tsx
// per Phase 8.2 task-27 — components own their option metadata.

const DEFAULT_INVOICE_PAGE_SIZE = 10;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatUsdCents(cents: number | null | undefined): string {
  const value = (cents ?? 0) / 100;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDueDate(invoice: AdminInvoiceRow): string {
  const iso = invoice.due_local_date ?? invoice.due_at;
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Pull a user-facing message out of any API envelope per
 * INTERFACE-STANDARDS frontend message priority chain
 * (error.user_message > error.message > fallback). Never returns
 * AxiosError.message / Response.statusText / "Request failed with status…".
 */
function extractApiMessage(
  payload: unknown,
  fallback: string,
): string {
  if (payload && typeof payload === 'object') {
    const env = payload as ApiErrorEnvelope;
    if (env.error?.user_message) return env.error.user_message;
    if (env.error?.message) return env.error.message;
  }
  return fallback;
}

function extractErrorCode(payload: unknown): string | null {
  if (payload && typeof payload === 'object') {
    const env = payload as ApiErrorEnvelope;
    if (env.error?.code) return env.error.code;
  }
  return null;
}

function newIdempotencyKey(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `idemp-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function unwrap<T>(body: unknown): T | undefined {
  if (body && typeof body === 'object') {
    if ('data' in body && (body as { data?: unknown }).data) {
      return (body as { data: T }).data;
    }
    return body as T;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// InvoiceStatusBadge — same 5-color scheme as Task 37
// ---------------------------------------------------------------------------

function InvoiceStatusBadge({
  status,
}: {
  status: InvoiceStatusEnum;
}): JSX.Element {
  const { t } = useTranslation('finance');
  let color: string;
  let label: string;
  switch (status) {
    case 'DRAFT':
      color = 'bg-blue-100 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800';
      label = t('merchant_billing.invoiceStatus.DRAFT');
      break;
    case 'SENT':
      color = 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800';
      label = t('merchant_billing.invoiceStatus.SENT');
      break;
    case 'PENDING_APPROVAL':
      color = 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800';
      label = t('merchant_billing.invoiceStatus.PENDING_APPROVAL');
      break;
    case 'PAID':
      color = 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
      label = t('merchant_billing.invoiceStatus.PAID');
      break;
    case 'PARTIAL':
      color = 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
      label = t('merchant_billing.invoiceStatus.PARTIAL');
      break;
    case 'OVERDUE':
      color = 'bg-rose-100 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-800';
      label = t('merchant_billing.invoiceStatus.OVERDUE');
      break;
    case 'VOIDED':
      color = 'bg-pw-surface text-pw-text border-pw-border';
      label = t('merchant_billing.invoiceStatus.VOIDED');
      break;
    default:
      color = 'bg-pw-surface text-pw-text border-pw-border';
      label = status;
      break;
  }
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
        color,
      )}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// ModeBadge — for the page header
// ---------------------------------------------------------------------------

function ModeBadge({ mode }: { mode: BillingMode }): JSX.Element {
  let color: string;
  switch (mode) {
    case 'PREPAID':
      color = 'bg-sky-100 dark:bg-sky-950/40 text-sky-800 dark:text-sky-300 border-sky-200 dark:border-sky-800';
      break;
    case 'CREDIT':
      color = 'bg-violet-100 dark:bg-violet-950/40 text-violet-800 dark:text-violet-300 border-violet-200 dark:border-violet-800';
      break;
    case 'INVOICE':
      color = 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800';
      break;
    default:
      color = 'bg-pw-surface text-pw-text border-pw-border';
      break;
  }
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium uppercase tracking-wide',
        color,
      )}
    >
      {mode}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function MerchantBillingPage(): JSX.Element {
  const { merchantId } = useParams<{ merchantId: string }>();
  const { t } = useTranslation('finance');
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const user = useAuth((s) => s.user);
  // debug-bill iter 13b — superadmin must also count as admin for the
  // billing-mode + billing-config write surfaces (BE adminWriteGuard
  // already accepts both). Previously this gate filtered superadmin to
  // false → mode dropdown + Save mode + Save config all rendered disabled.
  const isAdmin =
    user?.primaryRole === 'admin' || user?.primaryRole === 'superadmin';
  const isAccountant = user?.primaryRole === 'accountant';

  // ---- Server state ----
  const [merchant, setMerchant] = useState<MerchantSummary | null>(null);
  const [billingState, setBillingState] = useState<
    BillingConfigResponseBody | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // ---- Mode selector local state ----
  const [pendingMode, setPendingMode] = useState<BillingMode | null>(null);
  const [modeSubmitting, setModeSubmitting] = useState(false);
  const [reason, setReason] = useState('');

  // ---- Settlement dialog state ----
  const [settlementOpen, setSettlementOpen] = useState(false);
  const [settlementAction, setSettlementAction] =
    useState<SettlementAction | null>(null);
  const [activeCreditMemos, setActiveCreditMemos] = useState<CreditMemoRow[]>(
    [],
  );
  const [creditMemoTotalCents, setCreditMemoTotalCents] = useState(0);

  // ---- Billing config form state ----
  const [cfgCycle, setCfgCycle] = useState<InvoiceCycle>('MONTHLY');
  const [cfgDueDays, setCfgDueDays] = useState<string>('30');
  const [cfgDueDayType, setCfgDueDayType] = useState<DueDayType>('BUSINESS');
  const [cfgSoftLimitUsd, setCfgSoftLimitUsd] = useState<string>('');
  const [cfgPaymentMethods, setCfgPaymentMethods] = useState<PaymentMethod[]>(
    ['BANK_TRANSFER'],
  );
  const [cfgSubmitting, setCfgSubmitting] = useState(false);
  const [cfgFieldErrors, setCfgFieldErrors] = useState<Record<string, string>>(
    {},
  );

  // ---- Invoice history state ----
  const invoicePage = Math.max(
    1,
    Number.parseInt(searchParams.get('invoice_page') ?? '1', 10) || 1,
  );
  const [invoices, setInvoices] = useState<AdminInvoiceRow[]>([]);
  const [invoicePagination, setInvoicePagination] = useState<PaginationMeta>({
    page: 1,
    pageSize: DEFAULT_INVOICE_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  // ---- Fetch billing-config + merchant header ----
  useEffect(() => {
    if (!merchantId) return;
    let cancelled = false;
    setLoading(true);
    void (async (): Promise<void> => {
      try {
        const client = apiClient() as unknown as LooseClient;

        // Billing config + soft cap.
        const cfgRes = await client.GET(
          `/api/v1/admin/merchants/${encodeURIComponent(
            merchantId,
          )}/billing-config`,
        );
        if (cancelled) return;
        if (!cfgRes.response.ok || cfgRes.error) {
          toast({
            title: t('merchant_billing.errors.loadConfigFailed'),
            description: extractApiMessage(
              cfgRes.error,
              t('merchant_billing.errors.generic'),
            ),
            variant: 'error',
          });
          setBillingState(null);
        } else {
          const inner = unwrap<BillingConfigResponseBody>(cfgRes.data);
          if (inner) {
            setBillingState(inner);
            // Seed the form state from the server config.
            const cfg = inner.billing_config;
            if (cfg) {
              setCfgCycle(cfg.cycle);
              setCfgDueDays(String(cfg.due_days));
              setCfgDueDayType(cfg.due_day_type);
              setCfgSoftLimitUsd(
                cfg.soft_limit_usd_cents == null
                  ? ''
                  : String(cfg.soft_limit_usd_cents / 100),
              );
              // debug-bill iter 13 — collapse BE 9-value gateway list back
              // into FE 2-level categories (BANK_TRANSFER / GATEWAY) so the
              // checkboxes hydrate correctly after a save round-trip.
              setCfgPaymentMethods(
                (() => {
                  const hasBank = cfg.payment_methods.includes('bank' as unknown as PaymentMethod);
                  const hasGateway = cfg.payment_methods.some(
                    (m) => m !== ('bank' as unknown as PaymentMethod),
                  );
                  const cats: PaymentMethod[] = [];
                  if (hasBank) cats.push('BANK_TRANSFER');
                  if (hasGateway) cats.push('GATEWAY');
                  return cats.length > 0 ? cats : ['BANK_TRANSFER'];
                })(),
              );
            }
            setPendingMode(inner.billing_mode);
          }
        }

        // Merchant header — admin merchants list with id filter via search.
        // The list endpoint doesn't expose by-id GET in the contract slice;
        // pull from the list with pageSize=1 + post-filter on id (cheap
        // single-page admin call).
        // Bug 13 (debug-bill iter 9, 2026-05-04): BE schema uses `limit`,
        // not `pageSize` (adminMerchantsRead.handler.ts:151).
        const listRes = await client.GET(
          `/api/v1/admin/merchants?limit=100`,
        );
        if (cancelled) return;
        if (listRes.response.ok && !listRes.error) {
          const inner = unwrap<MerchantListResponseBody>(listRes.data);
          if (inner?.merchants) {
            const found = inner.merchants.find((m) => m.id === merchantId);
            if (found) setMerchant(found);
          }
        }
      } catch (err) {
        if (cancelled) return;
        toast({
          title: t('merchant_billing.errors.networkErrorTitle'),
          description:
            err instanceof Error && err.message
              ? err.message
              : t('merchant_billing.errors.networkError'),
          variant: 'error',
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `t` only feeds error-toast copy; adding it would refetch billing data on
    // every locale switch — intentionally excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, refreshTick, toast]);

  // ---- Fetch invoice history ----
  useEffect(() => {
    if (!merchantId) return;
    let cancelled = false;
    setInvoicesLoading(true);
    void (async (): Promise<void> => {
      try {
        const client = apiClient() as unknown as LooseClient;
        const qs = new URLSearchParams();
        qs.set('merchant_id', merchantId);
        qs.set('page', String(invoicePage));
        qs.set('pageSize', String(DEFAULT_INVOICE_PAGE_SIZE));
        const { data, error, response } = await client.GET(
          `/api/v1/admin/invoices?${qs.toString()}`,
        );
        if (cancelled) return;
        if (!response.ok || error) {
          toast({
            title: t('merchant_billing.errors.loadInvoicesFailed'),
            description: extractApiMessage(
              error,
              t('merchant_billing.errors.generic'),
            ),
            variant: 'error',
          });
          setInvoices([]);
          setInvoicePagination({
            page: 1,
            pageSize: DEFAULT_INVOICE_PAGE_SIZE,
            total: 0,
            totalPages: 0,
          });
          return;
        }
        const inner = unwrap<AdminInvoiceListResponseBody>(data);
        if (inner && Array.isArray(inner.invoices)) {
          setInvoices(inner.invoices);
          setInvoicePagination(
            inner.pagination ?? {
              page: invoicePage,
              pageSize: DEFAULT_INVOICE_PAGE_SIZE,
              total: inner.invoices.length,
              totalPages: 1,
            },
          );
        } else {
          setInvoices([]);
          setInvoicePagination({
            page: 1,
            pageSize: DEFAULT_INVOICE_PAGE_SIZE,
            total: 0,
            totalPages: 0,
          });
        }
      } catch (err) {
        if (cancelled) return;
        toast({
          title: t('merchant_billing.errors.networkErrorTitle'),
          description:
            err instanceof Error && err.message
              ? err.message
              : t('merchant_billing.errors.networkError'),
          variant: 'error',
        });
        setInvoices([]);
      } finally {
        if (!cancelled) setInvoicesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `t` only feeds error-toast copy; adding it would refetch the invoice
    // history on every locale switch — intentionally excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, invoicePage, refreshTick, toast]);

  // ---- Settlement preview — fetched on-demand when dialog opens ----
  const loadActiveCreditMemos = useCallback(async (): Promise<void> => {
    if (!merchantId) return;
    try {
      const client = apiClient() as unknown as LooseClient;
      const qs = new URLSearchParams();
      qs.set('merchant_id', merchantId);
      qs.set('status', 'ACTIVE');
      qs.set('pageSize', '50');
      const { data, error, response } = await client.GET(
        `/api/v1/admin/credit-memos?${qs.toString()}`,
      );
      if (!response.ok || error) {
        setActiveCreditMemos([]);
        setCreditMemoTotalCents(0);
        return;
      }
      const inner = unwrap<CreditMemoListResponseBody>(data);
      if (inner) {
        setActiveCreditMemos(inner.credit_memos ?? []);
        setCreditMemoTotalCents(
          inner.totals?.active_remaining_usd_cents ?? 0,
        );
      }
    } catch {
      // Non-fatal — dialog renders with empty preview list.
    }
  }, [merchantId]);

  // ---- URL helpers ----
  const setInvoicePage = useCallback(
    (n: number): void => {
      const next = new URLSearchParams(searchParams);
      if (n <= 1) next.delete('invoice_page');
      else next.set('invoice_page', String(n));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // ---- Derived ----
  const currentMode = billingState?.billing_mode ?? null;
  const currentVersion = billingState?.billing_mode_version ?? 0;
  const softCap = billingState?.soft_cap_usage ?? null;
  const grossOutstanding = softCap?.gross_outstanding_usd_cents ?? 0;
  const netOutstanding = softCap?.net_outstanding_usd_cents ?? 0;
  const softLimit = softCap?.soft_limit_usd_cents ?? null;
  const overCap = softCap?.over_cap ?? false;

  const modeIsDirty =
    pendingMode != null && currentMode != null && pendingMode !== currentMode;

  const requiresSettlement = useMemo<boolean>(() => {
    // Switching mode with outstanding > 0 OR active credit_memos requires
    // ModeSettlementDialog. Switching INVOICE→PREPAID with
    // active credit_memos always requires settlement, even with 0 outstanding.
    if (!modeIsDirty) return false;
    if (grossOutstanding > 0) return true;
    if (currentMode === 'INVOICE' && pendingMode !== 'INVOICE') {
      // The dialog will fetch credit_memos lazily; presume preview list might
      // surface options. Conservative: open dialog so the user can confirm.
      return true;
    }
    return false;
  }, [modeIsDirty, grossOutstanding, currentMode, pendingMode]);

  const remainingAfterCredits = Math.max(
    0,
    grossOutstanding - creditMemoTotalCents,
  );
  const insufficientCredit =
    settlementAction === 'apply_credits_then_switch' &&
    remainingAfterCredits > 0;

  // Wallet-frozen warning: PREPAID→INVOICE with positive wallet
  // shows a warning ("wallet will be frozen"). FE proxy: outstanding = 0 +
  // current PREPAID + switching to INVOICE → show warning. Wallet balance
  // not surfaced by billing-config endpoint, so we surface a generic warning
  // whenever the transition occurs.
  const showWalletFreezeWarning =
    modeIsDirty && currentMode === 'PREPAID' && pendingMode === 'INVOICE';

  // ---- Handlers ----

  async function handleSwitchMode(): Promise<void> {
    if (!merchantId || !pendingMode) return;
    if (!isAdmin) return; // UI guard; BE enforces auth
    if (requiresSettlement && !settlementOpen) {
      // Open dialog first; user will pick settlement_action then re-submit.
      await loadActiveCreditMemos();
      setSettlementOpen(true);
      return;
    }
    if (settlementAction === 'apply_credits_then_switch' && insufficientCredit) {
      // Block the actual API call until user picks another action.
      toast({
        title: t('merchant_billing.settlement.insufficientTitle'),
        description: t('merchant_billing.settlement.insufficientDesc', {
          amount: formatUsdCents(remainingAfterCredits),
        }),
        variant: 'warning',
      });
      return;
    }
    setModeSubmitting(true);
    try {
      const client = apiClient() as unknown as LooseClient;
      const { data, error, response } = await client.PUT(
        `/api/v1/admin/merchants/${encodeURIComponent(
          merchantId,
        )}/billing-mode`,
        {
          headers: { 'Idempotency-Key': newIdempotencyKey() },
          body: {
            new_mode: pendingMode,
            expected_version: currentVersion,
            settlement_action: settlementAction,
            reason: reason.trim() || undefined,
          },
        },
      );
      if (!response.ok || error) {
        const code = extractErrorCode(error);
        const message = extractApiMessage(
          error,
          t('merchant_billing.mode.switchFailed'),
        );
        if (code === 'OUTSTANDING_INVOICE_BLOCKS_SWITCH') {
          // Force open settlement dialog so user picks an action.
          await loadActiveCreditMemos();
          setSettlementOpen(true);
          toast({
            title: t('merchant_billing.settlement.requiredTitle'),
            description: message,
            variant: 'warning',
          });
        } else if (code === 'BILLING_MODE_VERSION_STALE') {
          toast({
            title: t('merchant_billing.mode.versionStaleTitle'),
            description: message,
            variant: 'warning',
          });
          setRefreshTick((tick) => tick + 1);
        } else {
          toast({
            title: t('merchant_billing.mode.switchFailed'),
            description: message,
            variant: 'error',
          });
        }
        return;
      }
      // Success — unwrap (silent — confirms persistence), refetch, close dialog.
      void unwrap<{ billing_mode: BillingMode; audit_log_id: string }>(data);
      toast({
        title: t('merchant_billing.mode.updatedTitle'),
        description: t('merchant_billing.mode.updatedDesc', {
          mode: pendingMode,
        }),
        variant: 'success',
      });
      setSettlementOpen(false);
      setSettlementAction(null);
      setReason('');
      setRefreshTick((tick) => tick + 1);
    } catch (err) {
      toast({
        title: t('merchant_billing.errors.networkErrorTitle'),
        description:
          err instanceof Error && err.message
            ? err.message
            : t('merchant_billing.errors.networkError'),
        variant: 'error',
      });
    } finally {
      setModeSubmitting(false);
    }
  }

  async function handleSaveConfig(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!merchantId) return;
    setCfgFieldErrors({});

    // Client-side validation matching contract Zod constraints.
    const dueDaysNum = Number.parseInt(cfgDueDays, 10);
    const errors: Record<string, string> = {};
    if (!Number.isFinite(dueDaysNum) || dueDaysNum < 1 || dueDaysNum > 90) {
      errors.due_days = t('merchant_billing.config.errors.dueDays');
    }
    if (cfgPaymentMethods.length === 0) {
      errors.payment_methods = t('merchant_billing.config.errors.paymentMethods');
    }
    let softLimitCents: number | null = null;
    if (cfgSoftLimitUsd.trim() !== '') {
      const parsed = Number.parseFloat(cfgSoftLimitUsd);
      if (!Number.isFinite(parsed) || parsed < 0) {
        errors.soft_limit_usd_cents = t('merchant_billing.config.errors.softLimit');
      } else {
        softLimitCents = Math.round(parsed * 100);
      }
    }
    if (Object.keys(errors).length > 0) {
      setCfgFieldErrors(errors);
      // BUG-A4 fix: surface validation feedback as toast in addition to
      // inline field text, so admins don't miss it when scrolled away from
      // the form input. Toast lists every failed field for one-glance triage.
      toast({
        title: t('merchant_billing.config.validationFailed'),
        description: Object.values(errors).join('. '),
        variant: 'error',
      });
      return;
    }

    setCfgSubmitting(true);
    try {
      const client = apiClient() as unknown as LooseClient;
      const { data, error, response } = await client.POST(
        `/api/v1/admin/merchants/${encodeURIComponent(
          merchantId,
        )}/billing-config`,
        {
          headers: { 'Idempotency-Key': newIdempotencyKey() },
          body: {
            cycle: cfgCycle,
            due_days: dueDaysNum,
            due_day_type: cfgDueDayType,
            soft_limit_usd_cents: softLimitCents,
            // debug-bill iter 13 — FE BillingConfigForm exposes a 2-level
            // category UI (BANK_TRANSFER / GATEWAY) but the BE enum is the
            // canonical 9-value gateway list (bank / stripe / paypal / …).
            // Map the categories to BE values before submit so save no
            // longer 400s with FST_ERR_VALIDATION on payment_methods/0.
            payment_methods: cfgPaymentMethods.flatMap((m) =>
              m === 'BANK_TRANSFER'
                ? (['bank'] as const)
                : m === 'GATEWAY'
                  ? ([
                      'stripe',
                      'paypal',
                      'payoneer',
                      'pingpong',
                      'lianlian',
                      'worldfirst',
                      'airwallex',
                      'sunrate',
                    ] as const)
                  : ([m] as const),
            ),
          },
        },
      );
      if (!response.ok || error) {
        // Map field_errors into per-field messages if present.
        if (
          error &&
          typeof error === 'object' &&
          (error as ApiErrorEnvelope).error?.field_errors
        ) {
          const fe = (error as ApiErrorEnvelope).error!.field_errors!;
          const flat: Record<string, string> = {};
          for (const [field, msgs] of Object.entries(fe)) {
            const first = Array.isArray(msgs) ? msgs[0] : undefined;
            if (first) flat[field] = first;
          }
          setCfgFieldErrors(flat);
        }
        toast({
          title: t('merchant_billing.config.saveFailed'),
          description: extractApiMessage(
            error,
            t('merchant_billing.errors.generic'),
          ),
          variant: 'error',
        });
        return;
      }
      void unwrap(data);
      toast({
        title: t('merchant_billing.config.savedTitle'),
        description: t('merchant_billing.config.savedDesc', {
          name: merchant?.name ?? t('merchant_billing.config.merchantFallback'),
        }),
        variant: 'success',
      });
      setRefreshTick((tick) => tick + 1);
    } catch (err) {
      toast({
        title: t('merchant_billing.errors.networkErrorTitle'),
        description:
          err instanceof Error && err.message
            ? err.message
            : t('merchant_billing.errors.networkError'),
        variant: 'error',
      });
    } finally {
      setCfgSubmitting(false);
    }
  }

  function togglePaymentMethod(method: PaymentMethod): void {
    setCfgPaymentMethods((prev) => {
      if (prev.includes(method)) {
        return prev.filter((m) => m !== method);
      }
      return [...prev, method];
    });
  }

  // ---- Render guards ----
  if (!merchantId) {
    return (
      <div className="p-6 text-sm text-pw-text-muted">
        {t('merchant_billing.errors.missingMerchantId')}
      </div>
    );
  }

  // ---- Render ----
  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Page header */}
      <PageHeader
        title={t('merchant_billing.title')}
        subtitle={
          <span className="flex items-center gap-2">
            {merchant ? (
              <>
                <span className="font-medium text-pw-text">{merchant.name}</span>
                <span>·</span>
                <span>{merchant.email}</span>
                {currentMode ? <ModeBadge mode={currentMode} /> : null}
              </>
            ) : (
              <span>
                {loading
                  ? t('merchant_billing.loading')
                  : t('merchant_billing.merchantWithId', { id: merchantId })}
              </span>
            )}
          </span>
        }
        actions={
          <a
            href={`/billing/invoices?merchant_id=${encodeURIComponent(
              merchantId,
            )}`}
            className="inline-flex h-10 items-center justify-center rounded-lg border border-pw-border bg-pw-bg px-4 text-sm font-medium text-pw-text transition-colors hover:bg-pw-surface"
          >
            {t('merchant_billing.viewAllInvoices')} →
          </a>
        }
      />

      {/* Top grid — 3 columns on lg, 1 column on sm/md */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* BillingModeSelector (extracted to features/billing/BillingModeSelector.tsx) */}
        <Card>
          <CardContent className="flex flex-col gap-4 p-6">
            <div>
              <h2 className="text-lg font-semibold text-pw-text">
                {t('merchant_billing.mode.heading')}
              </h2>
              <p className="text-xs text-pw-text-muted">
                {t('merchant_billing.mode.subheading')}
              </p>
            </div>
            <BillingModeSelector
              pendingMode={pendingMode}
              currentMode={currentMode}
              reason={reason}
              loading={loading}
              modeSubmitting={modeSubmitting}
              isAdmin={isAdmin}
              isAccountant={isAccountant}
              showWalletFreezeWarning={showWalletFreezeWarning}
              modeIsDirty={modeIsDirty}
              onPendingModeChange={(next) => setPendingMode(next)}
              onReasonChange={(next) => setReason(next)}
              onSubmit={() => {
                void handleSwitchMode();
              }}
            />
          </CardContent>
        </Card>

        {/* Soft-cap usage */}
        <Card>
          <CardContent className="flex flex-col gap-4 p-6">
            <div>
              <h2 className="text-lg font-semibold text-pw-text">
                {t('merchant_billing.outstanding.heading')}
              </h2>
              <p className="text-xs text-pw-text-muted">
                {t('merchant_billing.outstanding.subheading')}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-pw-text-muted">
                  {t('merchant_billing.outstanding.gross')}
                </div>
                <div className="text-2xl font-semibold text-pw-text">
                  {formatUsdCents(grossOutstanding)}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-pw-text-muted">
                  {t('merchant_billing.outstanding.net')}
                </div>
                <div className="text-xl font-medium text-pw-text">
                  {formatUsdCents(netOutstanding)}
                </div>
                <div className="text-xs text-pw-text-muted">
                  {t('merchant_billing.outstanding.netHint')}
                </div>
              </div>
              {softLimit != null ? (
                <div className="mt-2">
                  <div className="mb-1 flex items-center justify-between text-xs text-pw-text-muted">
                    <span>{t('merchant_billing.outstanding.softLimit')}</span>
                    <span>{formatUsdCents(softLimit)}</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-pw-surface">
                    <div
                      className={cn(
                        'h-full transition-all',
                        overCap
                          ? 'bg-rose-500'
                          : (netOutstanding / softLimit) >= 0.8
                          ? 'bg-amber-500'
                          : 'bg-emerald-500',
                      )}
                      style={{
                        width: `${Math.min(
                          100,
                          Math.round((netOutstanding / softLimit) * 100),
                        )}%`,
                      }}
                    />
                  </div>
                  {overCap ? (
                    <p className="mt-2 text-xs font-medium text-rose-700 dark:text-rose-300">
                      {t('merchant_billing.outstanding.overCap')}
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="text-xs text-pw-text-muted">
                  {t('merchant_billing.outstanding.noSoftLimit')}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* BillingConfigForm (extracted to features/billing/BillingConfigForm.tsx)
            — P8.D-491 due_day_type radio + D-491 verbatim help + BR-10
            withdraw toggle gated on Enterprise tier. */}
        <Card>
          <CardContent className="flex flex-col gap-4 p-6">
            <div>
              <h2 className="text-lg font-semibold text-pw-text">
                {t('merchant_billing.config.heading')}
              </h2>
              <p className="text-xs text-pw-text-muted">
                {t('merchant_billing.config.subheading')}
              </p>
            </div>
            <BillingConfigForm
              mode={currentMode}
              cycle={cfgCycle}
              dueDays={cfgDueDays}
              dueDayType={cfgDueDayType}
              softLimitUsd={cfgSoftLimitUsd}
              paymentMethods={cfgPaymentMethods}
              cfgSubmitting={cfgSubmitting}
              loading={loading}
              fieldErrors={cfgFieldErrors}
              tier={merchant?.tier ?? 'titanium'}
              withdrawEnabled={merchant?.withdraw_enabled ?? false}
              onCycleChange={(next) => setCfgCycle(next)}
              onDueDaysChange={(next) => setCfgDueDays(next)}
              onDueDayTypeChange={(next) => setCfgDueDayType(next)}
              onSoftLimitChange={(next) => setCfgSoftLimitUsd(next)}
              onTogglePaymentMethod={(m) => togglePaymentMethod(m)}
              onSubmit={(e) => {
                void handleSaveConfig(e);
              }}
            />
          </CardContent>
        </Card>
      </div>

      {/* MerchantInvoiceHistory */}
      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-pw-border px-6 py-4">
            <div>
              <h2 className="text-lg font-semibold text-pw-text">
                {t('merchant_billing.invoices.heading')}
              </h2>
              <p className="text-xs text-pw-text-muted">
                {t('merchant_billing.invoices.subheading')}
              </p>
            </div>
            <a
              href={`/billing/invoices?merchant_id=${encodeURIComponent(
                merchantId,
              )}`}
              className="text-sm font-medium text-pw-primary hover:text-pw-primary-hover"
            >
              {t('merchant_billing.invoices.viewAll')} →
            </a>
          </div>
          <div className="overflow-x-auto">
            <table
              className="w-full text-sm"
              data-testid="merchant-invoices-table"
            >
              <thead className="border-b border-pw-border bg-pw-surface/60 text-left text-xs font-medium uppercase tracking-wide text-pw-text-muted">
                <tr>
                  <th className="px-4 py-3">
                    {t('merchant_billing.invoices.columns.number')}
                  </th>
                  <th className="px-4 py-3">
                    {t('merchant_billing.invoices.columns.period')}
                  </th>
                  <th className="px-4 py-3">
                    {t('merchant_billing.invoices.columns.amount')}
                  </th>
                  <th className="px-4 py-3">
                    {t('merchant_billing.invoices.columns.status')}
                  </th>
                  <th className="px-4 py-3">
                    {t('merchant_billing.invoices.columns.due')}
                  </th>
                  <th className="w-[60px] px-4 py-3 text-right">
                    {t('merchant_billing.invoices.columns.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoicesLoading ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-12 text-center text-pw-text-muted"
                    >
                      {t('merchant_billing.invoices.loading')}
                    </td>
                  </tr>
                ) : invoices.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-12 text-center text-pw-text-muted"
                    >
                      {t('merchant_billing.invoices.empty')}
                    </td>
                  </tr>
                ) : (
                  invoices.map((row) => (
                    <tr
                      key={row.id}
                      data-testid={`merchant-invoices-row-${row.id}`}
                      className="border-b border-pw-border last:border-b-0 transition-colors hover:bg-pw-surface/50"
                    >
                      <td className="px-4 py-4 align-middle font-mono text-xs text-pw-text">
                        {row.invoice_number}
                      </td>
                      <td className="px-4 py-4 align-middle text-pw-text-muted">
                        {row.period_start?.slice(0, 10) ?? '—'} →{' '}
                        {row.period_end?.slice(0, 10) ?? '—'}
                      </td>
                      <td className="px-4 py-4 align-middle font-medium text-pw-text">
                        {formatUsdCents(row.total_usd_cents)}
                      </td>
                      <td className="px-4 py-4 align-middle">
                        <InvoiceStatusBadge status={row.status} />
                      </td>
                      <td className="px-4 py-4 align-middle text-pw-text">
                        {formatDueDate(row)}
                      </td>
                      <td className="px-4 py-4 align-middle text-right">
                        <a
                          href={`/admin/billing/invoices/${row.id}`}
                          className="inline-flex h-8 items-center rounded-md border border-pw-border px-2.5 text-xs font-medium text-pw-text-muted hover:bg-pw-surface hover:text-pw-text"
                        >
                          {t('merchant_billing.invoices.view')}
                        </a>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="border-t border-pw-border px-6 py-4">
            <Pagination
              currentPage={invoicePagination.page || invoicePage}
              pageCount={Math.max(1, invoicePagination.totalPages || 1)}
              totalRows={invoicePagination.total}
              windowSize={5}
              disabled={invoicesLoading}
              onChange={setInvoicePage}
              testId="merchant-billing-invoice-pagination"
            />
          </div>
        </CardContent>
      </Card>

      {/* ModeSettlementDialog (extracted to features/billing/ModeSettlementDialog.tsx) */}
      <ModeSettlementDialog
        open={settlementOpen}
        onOpenChange={(o) => setSettlementOpen(o)}
        pendingMode={pendingMode}
        settlementAction={settlementAction as DialogSettlementAction | null}
        onActionChange={(next) =>
          setSettlementAction(next as SettlementAction | null)
        }
        activeCreditMemos={activeCreditMemos.map((cm) => ({
          id: cm.id,
          amount_usd_cents: cm.amount_usd_cents,
        }))}
        creditMemoTotalCents={creditMemoTotalCents}
        grossOutstandingCents={grossOutstanding}
        remainingAfterCreditsCents={remainingAfterCredits}
        insufficientCredit={insufficientCredit}
        modeSubmitting={modeSubmitting}
        formatUsdCents={formatUsdCents}
        onConfirm={() => {
          void handleSwitchMode();
        }}
      />

    </div>
  );
}

export default MerchantBillingPage;
